import { reportingClient } from "../api/httpClients/reportingClient";
import {
  applyMacroScopeForRelatedUser,
  applyTemplateScopeForRelatedUser,
} from "./reportingListFilters";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import axios from "axios";
import { socketConfig } from "./authConfig";
import { getReportingApiBaseUrl } from "../config/apiBaseUrls";
import { DEV_DICOM_DOWNLOAD_REPORTING_BASE } from "../config/devDicomDownloadOverride";
import { resolveAuthEnvironment } from "../constants/authEnvironment";
import { AUTH_SOURCE, integrationAuthProvider } from "../constants/authSource";
import {
  appendCreOrganisationToAiPayload,
  getSelectedCreOrganisationId,
} from "../cre/creOrganisation";
import { useGlobalStore } from "../context/UserContext/userContext";
import { EDIT_REPORT_AI_ENDPOINT } from "../cre/editReportAi/editReportAiContract";

/** Adds OrganisationId for CRE only (same field as ProcessWithAI). */
function withCreOrganisationId(payload) {
  const { authSource, creSelectedOrganisationId, creUser } =
    useGlobalStore.getState();
  if (authSource !== AUTH_SOURCE.CRE) {
    return payload;
  }
  const organisationId = getSelectedCreOrganisationId(
    creUser,
    creSelectedOrganisationId,
  );
  return appendCreOrganisationToAiPayload(payload, organisationId);
}

function useCreTemplatesOrganisationId() {
  const authSource = useGlobalStore((s) => s.authSource);
  const creSelectedOrganisationId = useGlobalStore(
    (s) => s.creSelectedOrganisationId,
  );
  const creUser = useGlobalStore((s) => s.creUser);
  if (authSource !== AUTH_SOURCE.CRE) return null;
  return getSelectedCreOrganisationId(creUser, creSelectedOrganisationId);
}

export function getReportEngineDictionaryErrorMessage(error) {
  if (!error) return "";
  const data = error.response?.data;
  if (typeof data === "string" && data.trim()) {
    return data.trim();
  }
  if (data && typeof data === "object") {
    const fromBody =
      data.error || data.message || data.errorInfo?.message || "";
    if (String(fromBody).trim()) {
      return String(fromBody).trim();
    }
  }
  if (error.message && String(error.message).trim()) {
    return String(error.message).trim();
  }
  return "";
}

export async function appendReportEngineDictionaryPhrases({
  providerToken,
  language,
  anatomyCode,
  phrases,
}) {
  const provider = integrationAuthProvider(useGlobalStore.getState().authSource);
  const authEnvironment = resolveAuthEnvironment();
  const base = String(socketConfig?.url || "").replace(/\/$/, "");
  if (!base) {
    throw new Error("Missing socketConfig.url");
  }
  const url = `${base}/api/integrations/report-engine/dictionary/phrases`;
  // Authorized by the user's own token (providerToken), which the speech
  // server verifies with CRE / RADical — no shared secret in the web app.
  try {
    const response = await axios.post(
      url,
      {
        provider,
        providerToken,
        authEnvironment,
        language,
        anatomyCode,
        phrases,
        mode: "append",
      },
    );
    return response.data;
  } catch (error) {
    const detail = getReportEngineDictionaryErrorMessage(error);
    if (detail) {
      throw new Error(detail);
    }
    throw error;
  }
}

export async function downloadDicomStructuredReportHtml(accessionNumber) {
  const acc = String(accessionNumber || "").trim();
  if (!acc) throw new Error("Missing accessionNumber");

  const store = useGlobalStore.getState();
  const token = await store.getAccessToken();
  if (!token || !String(token).trim()) throw new Error("Missing access token");

  // TEMPORARY: CRE uses dev ReportingMicroservice; R4V unchanged (see devDicomDownloadOverride.js).
  const base = (
    store.authSource === AUTH_SOURCE.CRE
      ? DEV_DICOM_DOWNLOAD_REPORTING_BASE
      : getReportingApiBaseUrl()
  ).replace(/\/$/, "");
  // const base = getReportingApiBaseUrl().replace(/\/$/, "");
  const url = `${base}/api/Report/DownloadDicomJsonFile`;
  const res = await axios.get(url, {
    params: { accessionNumber: acc },
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
}

// Get Catalog
export const getCatalog = async (catalogType) => {
  const endpoint = "/api/Catalog/GetCatalog";

  try {
    const response = await reportingClient.post(endpoint, catalogType);
    console.log("API Response Data:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching catalog:", {
      message: error.message,
      config: error.config,
      response: error.response
        ? {
            status: error.response.status,
            data: error.response.data,
            headers: error.response.headers,
          }
        : null,
    });

    throw error;
  }
};
export const createReports = async (ReportObj) => {
  const endpoint = "/api/Report/CreateReport";

  try {
    const response = await reportingClient.post(endpoint, ReportObj);
    console.log("API Response Data:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching catalog:", {
      message: error.message,
      config: error.config,
      response: error.response
        ? {
            status: error.response.status,
            data: error.response.data,
            headers: error.response.headers,
          }
        : null,
    });

    throw error;
  }
};

// Get user permissions (Radical: userId, email, isAdmin, nreIsSaveConfirmation, …)
export const getUserPermissions = async () => {
  const endpoint = "/api/Report/GetUserPermissions";
  try {
    const response = await reportingClient.post(endpoint, {});
    return response.data;
  } catch (error) {
    console.error("GetUserPermissions failed:", error?.response?.data ?? error?.message);
    throw error;
  }
};

// Create Template
export const createTemplate = async (templateData) => {
  const endpoint = "/api/Report/CreateTemplateMulty";
  // console.log("Template Data:", templateData);
  // console.log("Access Token from api service:", accessToken);

  try {
    const response = await reportingClient.post(
      endpoint,
      withCreOrganisationId(templateData),
    );
    console.log("API Response Data:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error creating template:", {
      message: error.message,
      config: error.config,
      response: error.response
        ? {
            status: error.response.status,
            data: error.response.data,
            headers: error.response.headers,
          }
        : null,
    });
    throw error;
  }
};

// Check if Template Exists
export const isTemplateExists = async (templateName) => {
  const endpoint = "/api/Report/IsTemplateExists";

  try {
    const response = await reportingClient.post(endpoint, {
      TemplateName: templateName,
    });
    console.log("API Response Data:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error checking template existence:", {
      message: error.message,
      config: error.config,
      response: error.response
        ? {
            status: error.response.status,
            data: error.response.data,
            headers: error.response.headers,
          }
        : null,
    });

    throw error;
  }
};

export const useTemplates = (templatesRequest) => {
  const organisationId = useCreTemplatesOrganisationId();
  const request = {
    ...(templatesRequest || {}),
    ...(organisationId ? { OrganisationId: organisationId } : {}),
  };

  return useQuery({
    queryKey: ["template", request],
    queryFn: () => getTemplates(request),
    enabled: !!templatesRequest,
  });
};

export const getTemplates = async (templatesRequest) => {
  const raw = templatesRequest || {};
  const relatedUserIdForScope = raw.relatedUserId;

  const sanitizedRequest = { ...raw };
  Object.keys(sanitizedRequest).forEach((key) => {
    const value = sanitizedRequest[key];
    if (value === undefined || value === null) {
      delete sanitizedRequest[key];
    }
  });

  const response = await reportingClient.post(
    "/api/Report/GetTemplatesMulty",
    withCreOrganisationId(sanitizedRequest),
  );
  console.log("GET templates data", response.data);
  return applyTemplateScopeForRelatedUser(
    response.data.payload,
    relatedUserIdForScope
  );
};

// Get Template
export const getTemplate = async (templateId) => {
  const endpoint = "/api/Report/GetTemplatesMulty";
  try {
    const response = await reportingClient.post(endpoint, templateId);
    console.log("API Response Data:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching template:", {
      message: error.message,
      config: error.config,
      response: error.response
        ? {
            status: error.response.status,
            data: error.response.data,
            headers: error.response.headers,
          }
        : null,
    });
    throw error;
  }
};

//get single template
export const getSingleTemplate = async (templateId) => {
  const endpoint = "/api/Report/GetTemplate";

  try {
    const response = await reportingClient.post(endpoint, templateId);
    console.log("API Response Data:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching template:", {
      message: error.message,
      config: error.config,
      response: error.response
        ? {
            status: error.response.status,
            data: error.response.data,
            headers: error.response.headers,
          }
        : null,
    });

    throw error;
  }
};

/**
 * Per-user favorite flag for a template (CRE GetTemplatesMulty isFavorite).
 * @param {{ templateId: string, isFavorite: boolean }} body
 */
export const setFavoriteTemplate = async ({ templateId, isFavorite }) => {
  const endpoint = "/api/Report/SetFavoriteTemplate";
  const response = await reportingClient.post(
    endpoint,
    withCreOrganisationId({
      templateId,
      isFavorite: !!isFavorite,
    }),
  );
  if (response.data?.success === false) {
    const message =
      response.data?.errorInfo?.message ||
      response.data?.message ||
      "Failed to update favorite template";
    throw new Error(message);
  }
  return response.data;
};

//update template /Report/UpdateTemplate
export const updateTemplate = async (templateObj) => {
  const endpoint = "/api/Report/UpdateTemplateMulty";

  try {
    const response = await reportingClient.post(
      endpoint,
      withCreOrganisationId(templateObj),
    );
    console.log("API Response Data:", response.data);
    return response.data;
  } catch (error) {
    console.error("error updating template:", {
      message: error.message,
      config: error.config,
      response: error.response
        ? {
            status: error.response.status,
            data: error.response.data,
            headers: error.response.headers,
          }
        : null,
    });

    throw error;
  }
};

// Delete Template
export const deactivateTemplate = async (templateId) => {
  const endpoint = "/api/Report/DeactivateTemplate";

  try {
    const response = await reportingClient.post(endpoint, {
      TemplateId: templateId,
    });
    console.log("API Response Data:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error deactivating template:", {
      message: error.message,
      config: error.config,
      response: error.response
        ? {
            status: error.response.status,
            data: error.response.data,
            headers: error.response.headers,
          }
        : null,
    });

    throw error;
  }
};

// Create Macro
export const createMacro = async (macroData) => {
  const endpoint = "/api/Report/CreateMacroMulty";

  try {
    const response = await reportingClient.post(endpoint, macroData);
    console.log("API Response Data:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error creating macro:", {
      message: error.message,
      config: error.config,
      response: error.response
        ? {
            status: error.response.status,
            data: error.response.data,
            headers: error.response.headers,
          }
        : null,
    });
    throw error;
  }
};

// Check if Macro Exists
export const isMacroExists = async (macroName) => {
  const endpoint = "/api/Report/IsMacroExists";

  try {
    const response = await reportingClient.post(endpoint, {
      MacroName: macroName,
    });
    // console.log("API Response Data:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error checking macro existence:", {
      message: error.message,
      config: error.config,
      response: error.response
        ? {
            status: error.response.status,
            data: error.response.data,
            headers: error.response.headers,
          }
        : null,
    });

    throw error;
  }
};

export const useMacros = (macrosRequest) => {
  return useQuery({
    queryKey: ["macro", macrosRequest],
    queryFn: () => getMacros(macrosRequest),
    enabled: !!macrosRequest,
  });
};

export const getMacros = async (macrosRequest) => {
  const raw = macrosRequest || {};
  const relatedUserIdForScope = raw.relatedUserId;

  const response = await reportingClient.post(
    "/api/Report/GetMacrosMulty",
    raw
  );
  console.log("GET macros data", response.data);
  return applyMacroScopeForRelatedUser(
    response.data.payload,
    relatedUserIdForScope
  );
};

// Get Macro
export const getMacro = async (macroId) => {
  const endpoint = "/api/Report/GetMacrosMulty";

  try {
    const response = await reportingClient.post(endpoint, macroId);
    //  console.log("API Response Data:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching macro:", {
      message: error.message,
      config: error.config,
      response: error.response
        ? {
            status: error.response.status,
            data: error.response.data,
            headers: error.response.headers,
          }
        : null,
    });
    throw error;
  }
};

// Delete Macro
export const deleteMacro = async (macroId) => {
  const endpoint = "/api/Report/DeleteMacro";

  try {
    const response = await reportingClient.post(endpoint, { MacroId: macroId });
    console.log("API Response Data:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error deleting macro:", {
      message: error.message,
      config: error.config,
      response: error.response
        ? {
            status: error.response.status,
            data: error.response.data,
            headers: error.response.headers,
          }
        : null,
    });

    throw error;
  }
};

// Convert HTML to PDF
export const convertHtmlToPdf = async (file) => {
  const endpoint = "/api/Report/ConvertHtmlToPdf";

  const formData = new FormData();
  formData.append("file", file);

  try {
    // Do not set Content-Type manually for FormData; axios will set boundary.
    const response = await reportingClient.post(endpoint, formData);
    console.log("API Response Data:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error converting HTML to PDF:", {
      message: error.message,
      config: error.config,
      response: error.response
        ? {
            status: error.response.status,
            data: error.response.data,
            headers: error.response.headers,
          }
        : null,
    });

    throw error;
  }
};

export const useClientHeader = (templateSectionRequest) => {
  return useQuery({
    queryKey: ["clientHeader", templateSectionRequest],
    queryFn: () => getClientHeader1(templateSectionRequest),
    enabled: !!templateSectionRequest,
  });
};

export const getClientHeader1 = async (clientObj) => {
  const response = await reportingClient.post("/api/Report/GetClientHeader", clientObj);
  console.log("GET client header data", response.data);
  return response.data.payload;
};

// getClient Header
export const getClientHeader = async (clientObj) => {
  const endpoint = "/api/Report/GetClientHeader";

  try {
    const response = await reportingClient.post(endpoint, clientObj);
    console.log("API Response Data:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching client header:", {
      message: error.message,
      config: error.config,
      response: error.response
        ? {
            status: error.response.status,
            data: error.response.data,
            headers: error.response.headers,
          }
        : null,
    });
    throw error;
  }
};

// setClientHeader
export const setClientHeader = async (contentObj) => {
  const endpoint = "/api/Report/SetClientHeader";

  try {
    const response = await reportingClient.post(endpoint, contentObj);
    console.log("API Response Data:", response.data);
    return response.data;
  } catch (error) {
    console.error("error setting macro", {
      message: error.message,
      config: error.config,
      response: error.response
        ? {
            status: error.response.status,
            data: error.response.data,
            headers: error.response.headers,
          }
        : null,
    });

    throw error;
  }
};

export const useClientFooter = (templateSectionRequest) => {
  return useQuery({
    queryKey: ["clientFooter", templateSectionRequest],
    queryFn: () => getClientFooter1(templateSectionRequest),
    enabled: !!templateSectionRequest,
  });
};

export const getClientFooter1 = async (clientObj) => {
  const response = await reportingClient.post("/api/Report/GetClientFooter", clientObj);
  console.log("GET client footer data", response.data);
  return response.data.payload;
};

// getClientFooter
export const getClientFooter = async (clientObj) => {
  const endpoint = "/api/Report/GetClientFooter";

  try {
    const response = await reportingClient.post(endpoint, clientObj);
    console.log("API Response Data:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error deleting macro:", {
      message: error.message,
      config: error.config,
      response: error.response
        ? {
            status: error.response.status,
            data: error.response.data,
            headers: error.response.headers,
          }
        : null,
    });

    throw error;
  }
};

// setClientFooter
export const setClientFooter = async (contentObj) => {
  const endpoint = "/api/Report/SetClientFooter";

  try {
    const response = await reportingClient.post(endpoint, contentObj);
    console.log("API Response Data:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error deleting macro:", {
      message: error.message,
      config: error.config,
      response: error.response
        ? {
            status: error.response.status,
            data: error.response.data,
            headers: error.response.headers,
          }
        : null,
    });

    throw error;
  }
};
export const updateMacro = async (macroObj) => {
  const endpoint = "/api/Report/updateMacroMulty";

  try {
    const response = await reportingClient.post(endpoint, macroObj);
    console.log("API Response Data:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error deleting macro:", {
      message: error.message,
      config: error.config,
      response: error.response
        ? {
            status: error.response.status,
            data: error.response.data,
            headers: error.response.headers,
          }
        : null,
    });

    throw error;
  }
};

export const processWithAi = async (Aiobj) => {
  const endpoint = "/api/Report/ProcessWithAI";

  try {
    const response = await reportingClient.post(endpoint, Aiobj);
    console.log("API processWithAi Response Data:", response.data);
    if (response.data.success) {
      return response.data;
    }
    const info = response.data?.errorInfo;
    const code = info?.code || response.data?.errorCode;
    const message =
      (typeof info?.message === "string" && info.message) ||
      (typeof response.data?.message === "string" && response.data.message) ||
      "";
    if (code === "FEATURE_NOT_ALLOWED") {
      const err = new Error(message || "FEATURE_NOT_ALLOWED");
      err.code = "FEATURE_NOT_ALLOWED";
      err.errorCode = "FEATURE_NOT_ALLOWED";
      err.errorInfo = info;
      throw err;
    }
    if (message) {
      toast.error(message);
    }
  } catch (error) {
    console.error("Error generating Ai responce:", {
      message: error.message,
      config: error.config,
      response: error.response
        ? {
            status: error.response.status,
            data: error.response.data,
            headers: error.response.headers,
          }
        : null,
    });
    throw error;
  }
};

/**
 * AI Voice Editing (CRE). Returns the API envelope `{ success, payload }`.
 * Errors are thrown (with `code` when the API sends one); the caller shows them.
 */
export const editReportWithAi = async (body) => {
  const response = await reportingClient.post(EDIT_REPORT_AI_ENDPOINT, body);
  const data = response?.data;
  if (data?.success) return data;
  const info = data?.errorInfo;
  const code = info?.code || data?.errorCode || "EDIT_REPORT_AI_FAILED";
  const message =
    (typeof info?.message === "string" && info.message) ||
    (typeof data?.message === "string" && data.message) ||
    "";
  const err = new Error(message || code);
  err.code = code;
  err.errorInfo = info;
  throw err;
};

export const getBreeds = async () => {
  const endpoint = "/api/Report/GetBreeds";

  try {
    const response = await reportingClient.post(endpoint);
    console.log("API Response Data:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching breeds:", {
      message: error.message,
      config: error.config,
      response: error.response
        ? {
            status: error.response.status,
            data: error.response.data,
            headers: error.response.headers,
          }
        : null,
    });

    throw error;
  }
};

export const createWetRead = async (wetReadObj) => {
  const endpoint = "/api/Report/CreateWetRead";

  try {
    const response = await reportingClient.post(endpoint, wetReadObj);
    console.log("API Response Data for createWetRead:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error creating wet read:", {
      message: error.message,
      config: error.config,
      response: error.response
        ? {
            status: error.response.status,
            data: error.response.data,
            headers: error.response.headers,
          }
        : null,
    });

    throw error;
  }
};

export const updateWetRead = async (wetReadObj) => {
  const endpoint = "/api/Report/UpdateWetRead";

  try {
    const response = await reportingClient.post(endpoint, wetReadObj);
    console.log("API Response Data for updateWetRead:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error updating wet read:", {
      message: error.message,
      config: error.config,
      response: error.response
        ? {
            status: error.response.status,
            data: error.response.data,
            headers: error.response.headers,
          }
        : null,
    });

    throw error;
  }
};

export const getWetReadsForQuery = async () => {
  const response = await reportingClient.post("/api/Report/GetWetReads", {});
  console.log("GET wet reads data", response.data);
  return response.data.payload;
};

// Upload image to get media file URL
export const uploadImageToGetMediaFileUrl = async (file) => {
  try {
    // Convert file to base64
    const base64String = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    // Send JSON request with base64 data
    const requestData = {
      base64: base64String,
    };

    const response = await reportingClient.post("/api/Report/GetMediaFileUrl", requestData);

    const json = response.data;
    if (!json.success || !json.payload) {
      throw new Error("API did not return success or payload");
    }

    return json.payload;
  } catch (error) {
    console.error("Error uploading image:", {
      message: error.message,
      config: error.config,
      response: error.response
        ? {
            status: error.response.status,
            data: error.response.data,
            headers: error.response.headers,
          }
        : null,
    });

    throw error;
  }
};
